import React, {useEffect} from 'react';
import cx from 'classnames';
import {Flex, Icon, PriorityIcon, Text} from '@priority-software/priority-style-react';
import texts from '../../../../../../Localization/AISearch/texts';
import {EmptyResponseMsg} from '../EmptyResponseMsg/EmptyResponseMsg';
import {LoaderAnimation} from '../../../../../../features/EOYWizard/Components';
import {AIButton} from '../../../../../../components/AIButton/AIButton';
import {AIInput} from '../../../../../../components/AIInput/AIInput';
import styles from './styles.module.scss';
import {createDataTestId} from '../../../../../../utils/dataTestId';

type Props = {
    handleSearch: (value: string) => void;
    hasError: boolean;
    isLoading: boolean;
    isEmptyResult: boolean;
    showAiSearch: () => void;
    aiSearchVisible: boolean;
    componentClasses?: string;
};

export const AISearchBlock = (props: Props) => {
    const {
        handleSearch,
        isLoading,
        hasError,
        isEmptyResult,
        showAiSearch,
        aiSearchVisible,
        componentClasses
    } = props;
    const [value, setValue] = React.useState('');
    const focusRef = React.useRef<HTMLInputElement>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value);

    const onSearch = () => {
        if (!!value) {
            handleSearch(value);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            onSearch();
        }
    };

    const renderUXHelpers = () => {
        if (isEmptyResult) {
            return <EmptyResponseMsg showError={hasError} />;
        } else if (isLoading) {
            return (
                <Flex justifyContent="center" {...createDataTestId('loader-animation', true)}>
                    <LoaderAnimation />
                </Flex>
            );
        }
        // Show AI search description
        return (
            <Text textColor="secondary-500" skin="label-1">
                {texts.ai_search_description}
            </Text>
        );
    };

    useEffect(() => {
        if (aiSearchVisible && focusRef.current) {
            focusRef.current.focus();
        }
    }, [aiSearchVisible]);

    return (
        <div
            className={cx(styles.component, componentClasses)}
            {...createDataTestId('ai-search-block')}
        >
            <Flex direction="column" alignItems="flex-start" style={{gap: 16}}>
                {!aiSearchVisible ? (
                    <AIButton onClick={showAiSearch} text={texts.ai_label} />
                ) : (
                    <AIInput
                        ref={focusRef}
                        handleKeyDown={handleKeyDown}
                        onChange={handleChange}
                        onSearch={onSearch}
                        placeholder={texts.ai_input_placeholder}
                        value={value}
                        submitText={
                            <Icon
                                icon={PriorityIcon.paperPlane}
                                componentClasses={styles.gradientColor}
                                hoverable={true}
                            />
                        }
                    />
                )}

                {renderUXHelpers()}
            </Flex>
        </div>
    );
};
